import { FullLoader, IconButton, Modal, ModalFooter } from '@sc/shared-ui';
import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import GoogleIcon from '../assets/integrations/google.svg';
import DropboxIcon from '../assets/integrations/dropbox.svg';
import NotionIcon from '../assets/integrations/notion.svg';
import {
  ArrowUpTrayIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleStackIcon,
  LinkIcon,
  RocketLaunchIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Notion } from '../integrations/Notion';
import { Link, useLocation, useRouter } from '@tanstack/react-router';
import { Dropbox } from '../integrations/Dropbox';
import { GoogleDrive } from '../integrations/GoogleDrive';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GQL_MUTATION_UPDATE_USER_PREFERENCE,
  GQL_QUERY_INDEX_SOURCES,
  GQL_QUERY_INDEX_USER_PREFERENCES,
} from '../utility/queries';
import { PreferenceGroup, PreferenceName, SourceType } from '@sc/graphql';
import classNames from 'classnames';
import { URLs } from '../integrations/URLs';
import { File } from '../integrations/File';
import { track } from '../utility/analytics';

const SOURCE_TYPE_BY_HASH: Record<string, SourceType> = {
  'sources-google-drive': SourceType.googleDrive,
  'sources-notion': SourceType.notion,
  'sources-dropbox': SourceType.dropbox,
  'sources-url': SourceType.url,
  'sources-file': SourceType.file,
};

interface Props {
  uuid: string;
  queryUuid?: string | undefined;
}

const HINT_STEPS = [
  {
    Icon: SparklesIcon,
    title: 'Meet your AI knowledge base',
    description:
      'An AI knowledge base answers questions from your own content, in plain English. You control what it knows and who can reach it — set it up in three quick steps.',
  },
  {
    Icon: CircleStackIcon,
    title: 'Add sources',
    description:
      'Sources are what your knowledge base answers from. Connect Google Drive, Notion or Dropbox, add website URLs, or upload files — the content is read and used to answer questions.',
  },
  {
    Icon: ChatBubbleLeftRightIcon,
    title: 'Test your knowledge base',
    description:
      "Once your sources are added, ask your knowledge base questions to see how it answers. It's a quick way to check everything is working before anyone else sees it.",
  },
  {
    Icon: RocketLaunchIcon,
    title: 'Publish',
    description:
      'Configure who can use your knowledge base and how often they can ask questions. Publish on a hosted website address or add your own to share with your colleagues.',
  },
];

const ManageSourcesInner = ({
  uuid,
  queryUuid,
  selectedSource,
  isUploading,
  onUploadingChange,
}: Props & {
  selectedSource: string;
  isUploading: boolean;
  onUploadingChange: (isUploading: boolean) => void;
}) => {
  const router = useRouter();

  const { data } = useQuery(GQL_QUERY_INDEX_SOURCES, {
    variables: {
      projectUuid: uuid,
    },
    context: {
      isBatched: true,
    },
  });

  const counts = useMemo(
    () =>
      Object.values(SourceType).reduce(
        (prev, curr) => ({
          ...prev,
          [curr]:
            data?.indexSources.find((s) => s.type === curr)?.documentCount ?? 0,
        }),
        {} as Record<SourceType, number>
      ),
    [data?.indexSources]
  );

  const onClose = useCallback(
    () => void router.navigate({ to: '.', hash: '' }),
    [router]
  );

  return (
    <div className="flex flex-col overflow-hidden grow">
      <div className="flex overflow-hidden grow">
        <ul className="p-5 border-r border-grey-t-800 space-y-2">
          <li className="flex w-full">
            <Link
              to="."
              hash="sources-google-drive"
              data-selected={
                selectedSource === 'sources-google-drive' ? true : undefined
              }
              className="p-3 grow data-selected:bg-primary-400/10  flex items-center space-x-2 hover:bg-primary-400/10  rounded-xs"
            >
              <GoogleIcon />
              <div className="w-[180px] text-start grow">
                <span className="grow">Google Drive</span>
              </div>
              <div
                className={classNames(
                  'px-2 py-1 rounded-full flex items-center justify-center',
                  {
                    'border border-secondary-400 text-text-low-priority':
                      counts[SourceType.googleDrive],
                    'text-secondary-400': !counts[SourceType.googleDrive],
                  }
                )}
              >
                <span className="text-xs leading-4 font-bold">
                  {counts[SourceType.googleDrive]}
                </span>
              </div>
            </Link>
          </li>
          <li className="flex w-full">
            <Link
              to="."
              hash="sources-notion"
              data-selected={
                selectedSource === 'sources-notion' ? true : undefined
              }
              className="p-3 grow data-selected:bg-primary-400/10 flex items-center space-x-2 hover:bg-primary-400/10  rounded-xs"
            >
              <NotionIcon />
              <div className="w-[180px] text-start grow">
                <span className="grow">Notion</span>
              </div>
              <div
                className={classNames(
                  'px-2 py-1 rounded-full flex items-center justify-center',
                  {
                    'border border-secondary-400 text-text-low-priority':
                      counts[SourceType.notion],
                    'text-secondary-400': !counts[SourceType.notion],
                  }
                )}
              >
                <span className="text-xs leading-4 font-bold">
                  {counts[SourceType.notion]}
                </span>
              </div>
            </Link>
          </li>
          <li className="flex w-full">
            <Link
              to="."
              hash="sources-dropbox"
              data-selected={
                selectedSource === 'sources-dropbox' ? true : undefined
              }
              className="p-3 grow data-selected:bg-primary-400/10  flex items-center space-x-2 hover:bg-primary-400/10  rounded-xs"
            >
              <DropboxIcon />
              <div className="w-[180px] text-start grow">
                <span className="grow">Dropbox</span>
              </div>
              <div
                className={classNames(
                  'px-2 py-1 rounded-full flex items-center justify-center',
                  {
                    'border border-secondary-400 text-text-low-priority':
                      counts[SourceType.dropbox],
                    'text-secondary-400': !counts[SourceType.dropbox],
                  }
                )}
              >
                <span className="text-xs leading-4 font-bold">
                  {counts[SourceType.dropbox]}
                </span>
              </div>
            </Link>
          </li>
          <li className="flex w-full">
            <Link
              to="."
              hash="sources-url"
              data-selected={
                selectedSource === 'sources-url' ? true : undefined
              }
              className="p-3 grow data-selected:bg-primary-400/10  flex items-center space-x-2 hover:bg-primary-400/10  rounded-xs"
            >
              <LinkIcon className="size-5" />
              <div className="w-[180px] text-start grow">
                <span className="grow">URLs</span>
              </div>
              <div
                className={classNames(
                  'px-2 py-1 rounded-full flex items-center justify-center',
                  {
                    'border border-secondary-400 text-text-low-priority':
                      counts[SourceType.url],
                    'text-secondary-400': !counts[SourceType.url],
                  }
                )}
              >
                <span className="text-xs leading-4 font-bold">
                  {counts[SourceType.url]}
                </span>
              </div>
            </Link>
          </li>
          <li className="flex">
            <Link
              to="."
              hash="sources-file"
              data-selected={
                selectedSource === 'sources-file' ? true : undefined
              }
              className="p-3 data-selected:bg-primary-400/10  flex items-center space-x-2 hover:bg-primary-400/10  rounded-xs"
            >
              <ArrowUpTrayIcon className="size-5" />
              <div className="w-[180px] text-start">
                <span className="grow">File upload</span>
              </div>
              <div
                className={classNames(
                  'px-2 py-1 rounded-full flex items-center justify-center',
                  {
                    'border border-secondary-400 text-text-low-priority':
                      counts[SourceType.file],
                    'text-secondary-400': !counts[SourceType.file],
                  }
                )}
              >
                <span className="text-xs leading-4 font-bold">
                  {counts[SourceType.file]}
                </span>
              </div>
            </Link>
          </li>
        </ul>
        <div className="w-[600px] flex flex-col grow">
          <Suspense fallback={<FullLoader className="size-5" />}>
            {selectedSource === 'sources-notion' && (
              <Notion projectUuid={uuid} />
            )}
            {selectedSource === 'sources-dropbox' && (
              <Dropbox projectUuid={uuid} />
            )}
            {selectedSource === 'sources-google-drive' && (
              <GoogleDrive projectUuid={uuid} />
            )}
            {selectedSource === 'sources-url' && <URLs projectUuid={uuid} />}
            {selectedSource === 'sources-file' && (
              <File projectUuid={uuid} onUploadingChange={onUploadingChange} />
            )}
          </Suspense>
        </div>
      </div>
      <ModalFooter
        onClose={onClose}
        // submitText="Save sources"
        onSubmit={onClose}
        closeText={isUploading ? 'Uploading...' : 'Close'}
        buttonsDisabled={isUploading}
      />
    </div>
  );
};

export const ManageSources = ({ uuid, queryUuid }: Props): ReactElement => {
  const location = useLocation();
  const selectedSource = location.hash;
  const router = useRouter();

  const open = selectedSource.includes('sources');

  useEffect(() => {
    const sourceType = SOURCE_TYPE_BY_HASH[selectedSource];

    if (sourceType)
      track('Source Tab Viewed', { sourceType, projectUuid: uuid });
  }, [selectedSource, uuid]);

  // While documents are uploading the modal cannot be dismissed — an undefined
  // onClose hides the X button and disables backdrop/escape closing.
  const [isUploading, setIsUploading] = useState(false);

  const [hintDismissed, setHintDismissed] = useState(false);
  const [hintStep, setHintStep] = useState(0);
  const hintStepContent = HINT_STEPS[hintStep];

  const { data: preferencesData } = useQuery(GQL_QUERY_INDEX_USER_PREFERENCES, {
    variables: {
      input: {
        filter: {
          group: PreferenceGroup.hints,
        },
      },
    },
    skip: !open,
  });

  const [updateUserPreference] = useMutation(
    GQL_MUTATION_UPDATE_USER_PREFERENCE
  );

  const showSourcesHint =
    open &&
    !hintDismissed &&
    !!preferencesData &&
    !preferencesData.indexUserPreferences.some(
      (p) => p.preference.name === PreferenceName.configureSources && !!p.value
    );

  useEffect(() => {
    if (!showSourcesHint || !hintStepContent) return;

    track('Onboarding Step Viewed', {
      step: hintStepContent.title,
      stepIndex: hintStep,
    });
  }, [showSourcesHint, hintStepContent, hintStep]);

  const onDismissHint = useCallback(() => {
    setHintDismissed(true);

    track('Onboarding Completed');

    void updateUserPreference({
      variables: {
        input: {
          group: PreferenceGroup.hints,
          name: PreferenceName.configureSources,
          value: '1',
        },
      },
      refetchQueries: [GQL_QUERY_INDEX_USER_PREFERENCES],
    });
  }, [updateUserPreference]);

  return (
    <>
      <Modal
        open={open}
        onClose={
          isUploading
            ? undefined
            : () => void router.navigate({ to: '.', hash: '' })
        }
        dialogPanelClassname="h-200"
        title="Configure sources"
      >
        <ManageSourcesInner
          uuid={uuid}
          queryUuid={queryUuid}
          selectedSource={selectedSource}
          isUploading={isUploading}
          onUploadingChange={setIsUploading}
        />
      </Modal>
      <Modal
        open={showSourcesHint}
        onClose={onDismissHint}
        hideTopHr
        dialogPanelClassname="w-150"
      >
        {hintStepContent && (
          <div className="flex flex-col items-center px-10 pb-10">
            <div className="p-6 bg-primary-t-600/10 rounded-lg">
              <hintStepContent.Icon className="size-11 text-primary-300" />
            </div>
            <h2 className="font-grotesque text-h1-small font-semibold text-center pt-5 pb-4">
              {hintStepContent.title}
            </h2>
            <p className="text-center text-text-low-priority pb-8">
              {hintStepContent.description}
            </p>
            <div className="flex items-center justify-between w-full">
              <div className={classNames({ invisible: hintStep === 0 })}>
                <IconButton
                  Icon={ChevronLeftIcon}
                  type="tertiary"
                  size="large"
                  onClick={() => setHintStep((s) => Math.max(0, s - 1))}
                />
              </div>
              <span className="text-sm text-text-low-priority">
                {hintStep + 1} of {HINT_STEPS.length}
              </span>
              <IconButton
                Icon={
                  hintStep === HINT_STEPS.length - 1
                    ? CheckIcon
                    : ChevronRightIcon
                }
                type="primary"
                size="large"
                onClick={
                  hintStep === HINT_STEPS.length - 1
                    ? onDismissHint
                    : () => setHintStep((s) => s + 1)
                }
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};
